import React, {useState, useLayoutEffect, useRef} from 'react';
import _ from 'lodash';
import styled, {withTheme} from 'styled-components';
import parse from 'html-react-parser';
import replaceNewLinesForHtml from '../../utils/replaceNewLines';
import parseText from '../../utils/parseText';

const DEFAULT_CARD_WIDTH = 294;
const DEFAULT_CARD_GAP = 32;

const getCardWidth = theme => _.get(theme, ['teaserCarousel', 'cardWidth'], DEFAULT_CARD_WIDTH);
const getCardGap = theme => _.get(theme, ['teaserCarousel', 'cardGap'], DEFAULT_CARD_GAP);

function isHtml(value) {
  if (_.isNil(value)) {
    return false;
  }

  return _.trim(value).startsWith('<p>');
}

const StyledCarousel = styled.div`
  width: 0;
  min-width: 100%;
  margin-top: 17px;
  padding: ${props => _.get(props, ['theme', 'teaserCarousel', 'padding'], '0')};
  background: ${props => _.get(props, ['theme', 'teaserCarousel', 'backgroundColor'], 'transparent')};
`;

const StyledViewport = styled.div`
  width: 100%;
  overflow: hidden;
`;

const StyledTrack = styled.div`
  display: flex;
  flex-direction: ${props => props.alignRight ? 'row-reverse' : 'row'};
  transition: transform 0.3s ease;
  transform: translateX(${props => props.alignRight ? props.offset : -props.offset}px);
`;

const StyledCard = styled.div`
  flex: 0 0 ${props => getCardWidth(props.theme)}px;
  box-sizing: border-box;
  ${props => `margin-${props.alignRight ? 'left' : 'right'}: ${getCardGap(props.theme)}px;`}
  padding: 16px;
  background: ${props => _.get(props, ['theme', 'teaserCarousel', 'cardBackgroundColor'], '#fff')};
  border-radius: ${props => _.get(props, ['theme', 'teaserCarousel', 'cardBorderRadius'], '4px')};
  box-shadow: ${props => _.get(props, ['theme', 'teaserCarousel', 'cardShadow'], 'rgba(0, 0, 0, 0.1) 1px 1px 4px 0px')};
`;

const StyledCardHeader = styled.div`
  && {
    color: ${props => _.get(props, ['theme', 'teaserCarousel', 'headerColor'], '#000')};
  }
  font-family: ${props => _.get(props, ['theme', 'teaserCarousel', 'headerFontFamily'], 'inherit')};
  font-size: ${props => _.get(props, ['theme', 'teaserCarousel', 'headerFontSize'], '40px')};
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 16px;
`;

const StyledCardBody = styled.div`
  &&,
  && * {
    color: ${props => _.get(props, ['theme', 'teaserCarousel', 'bodyColor'], '#000')};
  }
  font-family: ${props => _.get(props, ['theme', 'teaserCarousel', 'bodyFontFamily'], 'inherit')};
  font-size: ${props => _.get(props, ['theme', 'teaserCarousel', 'bodyFontSize'], '16px')};
  line-height: ${props => _.get(props, ['theme', 'teaserCarousel', 'bodyLineHeight'], '24px')};

  p {
    margin: 0 0 8px 0;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const StyledButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 32px;
`;

const StyledArrowButton = styled.button`
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  background: ${props => _.get(props, ['theme', 'teaserCarousel', 'buttonBackgroundColor'], '#fff')};
  box-shadow: ${props => _.get(props, ['theme', 'teaserCarousel', 'cardShadow'], 'rgba(0, 0, 0, 0.1) 1px 1px 4px 0px')};
  font-size: 24px;
  line-height: 1;
  cursor: pointer;

  && {
    color: ${props => _.get(props, ['theme', 'teaserCarousel', 'buttonColor'], '#000')};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

function TeaserCarousel({items, theme, formContext, alignRight}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const viewportRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(null);

  const cards = _.filter(items, item =>
    !_.isEmpty(_.get(item, 'teaserCarouselHeaderText')) || !_.isEmpty(_.get(item, 'teaserCarouselBodyTextHtml_txt')));

  useLayoutEffect(() => {
    const updateWidth = () => {
      const el = viewportRef.current;
      if (!el) {
        return;
      }
      const {left, right} = el.getBoundingClientRect();
      setViewportWidth(alignRight ? right : document.documentElement.clientWidth - left);
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [cards.length, alignRight]);

  if (_.isEmpty(cards)) {
    return null;
  }

  const renderBody = body => {
    const textParsed = parseText(formContext || {}, body);
    const bodyHtml = isHtml(textParsed) ? textParsed : replaceNewLinesForHtml(textParsed);
    return parse(bodyHtml);
  };

  return (
    <StyledCarousel>
      <StyledViewport
        ref={viewportRef}
        style={_.isNil(viewportWidth) ? undefined : {
          width: `${viewportWidth}px`,
          marginLeft: alignRight ? `calc(100% - ${viewportWidth}px)` : undefined
        }}>
        <StyledTrack alignRight={alignRight} offset={currentIndex * (getCardWidth(theme) + getCardGap(theme))}>
          {cards.map((item, index) => (
            <StyledCard key={index} alignRight={alignRight}>
              {!_.isEmpty(item.teaserCarouselHeaderText) && <StyledCardHeader>{item.teaserCarouselHeaderText}</StyledCardHeader>}
              {!_.isEmpty(item.teaserCarouselBodyTextHtml_txt) && <StyledCardBody>{renderBody(item.teaserCarouselBodyTextHtml_txt)}</StyledCardBody>}
            </StyledCard>
          ))}
        </StyledTrack>
      </StyledViewport>
      {cards.length > 1 && (
        <StyledButtons>
          <StyledArrowButton
            type='button'
            aria-label='Vorige'
            disabled={currentIndex === 0}
            onClick={() => setCurrentIndex(currentIndex - 1)}>
            &#8249;
          </StyledArrowButton>
          <StyledArrowButton
            type='button'
            aria-label='Volgende'
            disabled={currentIndex === cards.length - 1}
            onClick={() => setCurrentIndex(currentIndex + 1)}>
            &#8250;
          </StyledArrowButton>
        </StyledButtons>
      )}
    </StyledCarousel>
  );
}

export default withTheme(TeaserCarousel);
